/**
 * Runway Monitor — the production-flow primary view.
 *
 * Days of buffer per open job (customer date − expected remaining cycle), sorted
 * ascending and dollar-weighted: the daily action list. Plus gate alerts and the
 * exception queues. Data comes from the amtrend-production-flow snapshot project
 * via /api/production/* (read-only, behind app auth).
 *
 * Design note: this is a RUNWAY MONITOR, not a scheduler or a load heatmap.
 * Grade-C cycle estimates render as a RANGE, never a false point — same rule the
 * fitted standards enforce, so the UI never shows more confidence than the data has.
 */
import { useState, useEffect, useMemo, Fragment } from 'react'
import { Link } from 'react-router-dom'
import {
  AlertTriangle, Clock, DollarSign, Gauge, PackageX, Ban, Loader2, TrendingDown, Boxes, FileCheck2, ArrowLeft,
  ChevronRight, ChevronDown, ChevronLeft, X, PauseCircle, PenTool, ListChecks, Search, BookOpen, FileWarning,
} from 'lucide-react'
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, ReferenceLine,
} from 'recharts'
import {
  getRunwaySummary, getRunwayList, getRunwayOrders, getRunwayExceptions, getRunwayLateCurve, getRiskBands, getModeRisk,
} from '../lib/api'
import { formatCurrency, formatDate } from '../lib/formatters'

const TABS = [
  { id: 'runway', label: 'Runway', icon: Gauge },
  { id: 'com', label: 'COM outstanding', icon: PackageX },
  { id: 'stalled', label: 'Stalled jobs', icon: Clock },
  { id: 'unbuilt', label: 'Not building', icon: PauseCircle },
  { id: 'drafting', label: 'Drafting', icon: PenTool },
  { id: 'gradec', label: 'Booking discipline', icon: TrendingDown },
  { id: 'exiting', label: 'Exiting vendor', icon: Ban },
  { id: 'partgate', label: 'Frame gate', icon: Boxes },
  { id: 'approval', label: 'Approval loop', icon: FileCheck2 },
  { id: 'validation', label: 'Why this works', icon: AlertTriangle },
]

// The 8 exception queues fold under one "Exceptions ▾" menu so the bar isn't 10 wide.
const EXC_IDS = ['com', 'stalled', 'unbuilt', 'drafting', 'gradec', 'exiting', 'partgate', 'approval']

function tabBtnCls(active) {
  return `flex items-center gap-2 px-4 py-2 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors ${
    active
      ? 'border-amtrend-blue text-amtrend-blue font-medium'
      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
  }`
}

function TabCount({ n, active }) {
  if (n == null) return null
  return (
    <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${
      active ? 'bg-amtrend-blue/15 text-amtrend-blue' : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
    }`}>{n}</span>
  )
}

function runwayColor(days, overdue) {
  if (overdue || days < 0) return 'text-red-600 dark:text-red-400 font-semibold'
  if (days < 15) return 'text-orange-600 dark:text-orange-400 font-semibold'
  if (days < 45) return 'text-amber-600 dark:text-amber-400'
  return 'text-emerald-600 dark:text-emerald-400'
}

const BAND_TONE = {
  critical: 'bg-red-200 text-red-900 dark:bg-red-900/60 dark:text-red-200',
  high: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  watch: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  clear: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
}

// Low-confidence (grade C / range) estimates: outlined + dashed, never a solid fill,
// so a grade-C "Clear" reads as "uncertain", not a confident all-clear. This is the
// fix for the 25491-style inversion (vendor-fallback ETA painted as a confident band).
const BAND_TONE_MUTED = {
  critical: 'border border-dashed border-red-400/60 text-red-600 dark:text-red-300 bg-transparent',
  high: 'border border-dashed border-red-400/50 text-red-600 dark:text-red-300 bg-transparent',
  watch: 'border border-dashed border-amber-400/60 text-amber-700 dark:text-amber-300 bg-transparent',
  clear: 'border border-dashed border-emerald-400/60 text-emerald-700 dark:text-emerald-300 bg-transparent',
}

function bandRangeLabel(b) {
  const lo = b.lo_days, hi = b.hi_days
  const f = (n) => (n < 0 ? `−${Math.abs(n)}` : `+${n}`)
  if (lo == null) return `< ${f(hi)}d`
  if (hi == null) return `≥ ${f(lo)}d`
  return `${f(lo)}…${f(hi)}d`
}

// Buffer shown as a backtest-calibrated risk band; raw days kept muted (rank still matters).
// lowConf (grade C / range estimate) → outlined "~" band, not a solid fill, so a
// low-confidence estimate never masquerades as a confident band.
function RiskBand({ bandKey, bandMap, p50, p80, lowConf }) {
  const b = bandMap && bandMap[bandKey]
  // Buffers are computed doubles — round for display so we never show -20.400000000000006.
  const rnd = (n) => (n == null || isNaN(Number(n)) ? '–' : Math.round(Number(n)))
  const raw = lowConf && p80 != null ? `${rnd(p80)}…${rnd(p50)}` : `${rnd(p50)}`
  if (!b) return <span className="font-mono">{raw}</span>
  const cls = lowConf ? (BAND_TONE_MUTED[b.tone] || '') : (BAND_TONE[b.tone] || '')
  return (
    <div className="flex items-center gap-2 whitespace-nowrap">
      <span
        className={`px-2 py-0.5 rounded text-xs font-semibold ${cls}`}
        title={lowConf
          ? `${b.label} — LOW CONFIDENCE (grade C: range estimate / vendor fallback). Rank shown, but treat the exact value as uncertain. Baseline ${b.late_pct}% late.`
          : `${b.label} — ${b.late_pct}% of such jobs shipped late historically (baseline 43%)`}
      >
        {lowConf ? '~' : ''}{b.short}
      </span>
      <span className="text-xs text-gray-400 dark:text-gray-500 font-mono">{raw}d</span>
    </div>
  )
}

function RiskLegend({ bands }) {
  if (!bands || !bands.length) return null
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-surface-dark p-4 text-sm">
      <div className="font-medium text-gray-900 dark:text-gray-100">
        Buffer is a risk rank, not a day count
      </div>
      <p className="text-gray-600 dark:text-gray-300 mt-1">
        Buffer = promised date − expected remaining cycle. The exact negative number carries little decision once
        you're behind, so it's binned into bands calibrated to how often such jobs actually shipped late
        (baseline: <b>43%</b> of all jobs ship late).
      </p>
      <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2">
        {bands.map((b) => (
          <div key={b.band_key} className="flex items-center gap-2">
            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${BAND_TONE[b.tone] || ''}`}>{b.short}</span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {bandRangeLabel(b)} · <b>{b.late_pct}%</b> late
            </span>
          </div>
        ))}
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
        An <span className="px-1.5 py-0.5 rounded text-[11px] font-semibold border border-dashed border-gray-400/60">~outlined</span>{' '}
        band means a <b>grade-C / low-confidence</b> estimate (a range, e.g. a vendor-fallback import ETA): trust the rank,
        not the exact number. Solid bands are grade A/B.
      </p>
    </div>
  )
}

// Finished good (arrives complete — receipt is the finish line) vs component
// (in-house build continues after receipt). From derived.job_osfab_kind.
function KindBadge({ kind, region }) {
  if (!kind) return null
  const finished = kind === 'finished'
  const cls = finished
    ? 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300'
    : 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300'
  const reg = region && region !== 'domestic' ? ` · ${region}` : ''
  return (
    <span
      className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${cls}`}
      title={finished
        ? `Finished good (${region}) — receipt is the finish line, no floor capacity needed`
        : `Component (${region}) — in-house build continues after receipt, reserve capacity`}
    >
      {finished ? 'Finished' : 'Component'}{reg}
    </span>
  )
}

function basisLabel(basis) {
  if (!basis) return '—'
  if (basis.startsWith('frame ETA (promise')) return 'frame (promise)'
  if (basis.startsWith('frame ETA')) return 'frame (est.)'
  if (basis.startsWith('frames received')) return 'finishing'
  if (basis.includes('order to complete')) return 'stage (pre-gate)'
  return 'stage'
}

function ProjectCell({ project, customer }) {
  return (
    <div className="min-w-[10rem]">
      <div className="text-gray-900 dark:text-gray-100">{project || '—'}</div>
      {customer && <div className="text-xs text-gray-500">{customer}</div>}
    </div>
  )
}

function GradeBadge({ grade }) {
  const map = {
    A: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
    B: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
    C: 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  }
  return <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${map[grade] || map.C}`}>{grade}</span>
}

function StatCard({ icon: Icon, label, value, tone = 'default', onClick, active }) {
  const tones = {
    default: 'text-gray-900 dark:text-gray-100',
    danger: 'text-red-600 dark:text-red-400',
    warn: 'text-amber-600 dark:text-amber-400',
    money: 'text-amtrend-blue',
  }
  const clickable = !!onClick
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!clickable}
      className={`text-left w-full rounded-lg border p-4 transition-colors bg-white dark:bg-surface-dark
        ${active ? 'border-amtrend-blue ring-1 ring-amtrend-blue' : 'border-gray-200 dark:border-gray-700'}
        ${clickable ? 'hover:border-amtrend-blue cursor-pointer' : 'cursor-default'}`}
    >
      <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-xs font-medium uppercase tracking-wide">
        <Icon className="w-4 h-4" /> {label}
      </div>
      <div className={`mt-2 text-2xl font-bold ${tones[tone]}`}>{value}</div>
    </button>
  )
}

const ORDER_FILTERS = {
  all:          { label: 'All open orders', order: () => true, job: () => true },
  overdue:      { label: 'Overdue', order: (o) => o.any_overdue, job: (j) => j.promise_overdue },
  insufficient: { label: 'Insufficient runway', order: (o) => o.order_runway_p50 < 0 && !o.any_overdue, job: (j) => j.runway_p50 < 0 && !j.promise_overdue },
  gate:         { label: 'Gate < 45d', order: (o) => o.alert_gate_low_runway, job: (j) => j.alert_gate_low_runway },
  norelease:    { label: 'No RELEASE 14d+', order: (o) => o.alert_no_release_14d, job: (j) => j.alert_no_release_14d },
  frame:        { label: 'Frame overdue', order: (o) => o.alert_frame_overdue, job: (j) => j.alert_frame_overdue },
}

/* ── Today worklist ─────────────────────────────────────────────────────────
 * The action-first landing view: the same open orders, but grouped by the single
 * cheapest LEVER (not by buffer), sorted by expected dollars-late (order value ×
 * calibrated band late-rate), each with a today→promise→P50-P80 completion bar.
 * Pure client-side over the /runway-orders data already in state — no new endpoint.
 */
const BAND_RATE_FALLBACK = { critical: 0.96, at_risk: 0.52, on_plan: 0.35 }
function bandRate(band, bandMap) {
  const b = bandMap && bandMap[band]
  if (b && b.late_pct != null) return b.late_pct / 100
  return BAND_RATE_FALLBACK[band] ?? 0.5
}
// Per-mode band recalibration: the global bands are domestic-calibrated, so import
// modes get scaled up (empirical overall late-ratios; see config.mode_risk). Seeded
// here and overwritten from /mode-risk on load. Module-level so elossOf stays a pure
// (o, bandMap) call everywhere it's used.
let MODE_MULT = { domestic: 1, nearshore_mx: 1.14, full_import: 1.2, hybrid: 1, import_unmapped: 1 }

// Expected dollars-late = customer order value × band late-rate × mode multiplier.
// order_value (open-line qty×unit_price) is real revenue — NOT total_dollars, which is
// labor $ at risk and left 39% of orders (import/outsourced, no in-house labor) unrankable.
function elossOf(o, bandMap) {
  return (o.order_value || 0) * bandRate(o.risk_band, bandMap) * (MODE_MULT[o.binding_mode] ?? 1)
}
// Blocker-led bucket: what to DO. Critical → renegotiate (unsavable) takes priority
// over "release now" even when a critical order is also unreleased.
function todayBucket(o) {
  if (o.risk_band === 'critical') return 'renegotiate'
  if (o.alert_no_release_14d && o.order_runway_p50 < 0) return 'release'
  if (o.order_runway_p50 < 0) return 'expedite'
  return 'onplan'
}

// Expedite is the largest bucket (~290 orders) — facet it by the dominant blocker so
// it reads as sub-queues, not one wall. Past-promise first (hardest fact), then the
// gate signal, then the quiet 'projected behind with no flag' — often the big-dollar ones.
function expediteFacet(o) {
  if (o.any_overdue) return 'overdue'
  if (o.alert_gate_low_runway) return 'gate'
  return 'behind'
}
const EXPEDITE_FACETS = [
  { key: 'overdue', label: 'Past promise — already late',
    blurb: 'Customer date has passed but the buffer is still recoverable. Expedite hard, or open a date conversation.' },
  { key: 'gate', label: 'Cleared the gate < 45d',
    blurb: 'On the floor with little runway — a shop-throughput push, not a sourcing problem.' },
  { key: 'behind', label: 'Behind — no discrete blocker',
    blurb: 'Negative only from the remaining-cycle projection: no missing release, gate, or vendor flag. The quiet, often high-dollar ones.' },
]

const BUCKET_META = [
  { key: 'renegotiate', title: 'Renegotiate the date', tone: 'critical', initial: 8,
    blurb: "Expected completion is so far past the promise that expediting won't recover it (~96% land late). Escalate to sales/customer for a new date." },
  { key: 'release', title: 'Release now', tone: 'release', initial: 8,
    blurb: "No signed RELEASE 14+ days after order — the job can't start until it's released. The cheapest fix on the board." },
  { key: 'expedite', title: 'Expedite through the shop', tone: 'at_risk', initial: 8,
    facetFn: expediteFacet, facets: EXPEDITE_FACETS,
    blurb: "Behind the promise but still savable (~52% late without action), split by the dominant blocker. A “chase vendor” badge flags a frame/COM receipt as the specific hold." },
  { key: 'onplan', title: 'On plan', tone: 'on_plan', initial: 5,
    blurb: "Best cohort — expected to make the date. Not “safe”: ~34% of these still slip, so scan the top-dollar ones." },
]

const BUCKET_DOT = {
  critical: 'bg-red-500', release: 'bg-blue-500', at_risk: 'bg-amber-500', on_plan: 'bg-emerald-500',
}
const BUCKET_BORDER = {
  critical: 'border-t-2 border-t-red-500', release: 'border-t-2 border-t-blue-500',
  at_risk: 'border-t-2 border-t-amber-500', on_plan: 'border-t-2 border-t-emerald-500',
}
const WIN_FILL = { critical: '#dc2626', at_risk: '#d97706', on_plan: '#059669' }

// today → promise ▲ → P50-P80 expected-completion window. Shared clamped day axis
// so bars are comparable; overflow shown with a chevron. Hatched window = low-confidence.
const DMIN = -30, DMAX = 140
function dayPct(d) {
  const c = Math.max(DMIN, Math.min(DMAX, d))
  return ((c - DMIN) / (DMAX - DMIN)) * 100
}
function TimelineBar({ o }) {
  const now = new Date(); now.setHours(0, 0, 0, 0)
  const prom = new Date(o.promise_date)
  const dtp = Math.round((prom - now) / 86400000)
  const c50 = dtp - o.order_runway_p50
  const c80 = dtp - o.order_runway_p80
  const xToday = dayPct(0), xProm = dayPct(dtp)
  const a = dayPct(c50), b = dayPct(c80)
  const lo = Math.min(a, b), hi = Math.max(a, b)
  const winW = Math.max(hi - lo, 1.5)
  const late = o.order_runway_p50 < 0
  const lowConf = o.any_range
  const winStyle = lowConf
    ? { left: `${lo}%`, width: `${winW}%`, backgroundImage: 'repeating-linear-gradient(45deg,#c9d0da,#c9d0da 3px,#eef1f5 3px,#eef1f5 6px)', border: '1px solid #b9c2d0' }
    : { left: `${lo}%`, width: `${winW}%`, background: WIN_FILL[o.risk_band] || '#6b7280' }
  return (
    <div className="relative">
      <div className="relative h-6 rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 overflow-visible">
        {/* today */}
        <div className="absolute top-0 h-full w-px bg-gray-400 z-10" style={{ left: `${xToday}%` }} />
        {/* miss span (promise → completion) when late */}
        {late && xProm < lo && (
          <div className="absolute z-0" style={{ top: '9px', height: '6px', left: `${xProm}%`, width: `${lo - xProm}%`, backgroundImage: 'repeating-linear-gradient(45deg,#f3c9c2,#f3c9c2 3px,#fdece9 3px,#fdece9 6px)' }} />
        )}
        {/* completion window */}
        <div className="absolute rounded-sm z-[5]" style={{ top: '5px', height: '14px', ...winStyle }} />
        {/* promise tick */}
        <div className={`absolute top-0 h-full w-px z-20 ${late ? 'bg-red-600' : 'bg-gray-700 dark:bg-gray-200'}`} style={{ left: `${xProm}%` }} />
        {(c80 > DMAX) && <span className="absolute -right-2 top-0.5 text-red-600 font-extrabold leading-none">›</span>}
        {(dtp < DMIN) && <span className="absolute -left-2 top-0.5 text-gray-400 font-extrabold leading-none">‹</span>}
      </div>
      <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
        <span>today</span><span>promise {formatDate(o.promise_date)}</span>
      </div>
    </div>
  )
}

// Sourcing-mode badge — "is this slip recoverable at a glance". Import modes are the
// non-recoverable ones (fixed sailing), so they carry the loud colors.
const MODE_BADGE = {
  domestic:       { label: 'domestic',   cls: 'border-gray-300 text-gray-600 bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700' },
  nearshore_mx:   { label: 'nearshore MX', cls: 'border-amber-300 text-amber-700 bg-amber-50 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800' },
  full_import:    { label: 'full import', cls: 'border-red-300 text-red-700 bg-red-50 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800' },
  hybrid:         { label: 'hybrid', cls: 'border-purple-300 text-purple-700 bg-purple-50 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800' },
  import_unmapped:{ label: 'import?', cls: 'border-dashed border-red-300 text-red-600 dark:text-red-300 dark:border-red-800' },
}

function TodayChips({ o }) {
  const mode = MODE_BADGE[o.binding_mode]
  return (
    <div className="mt-1 flex flex-wrap gap-1">
      {mode && (
        <span className={`px-1.5 py-px rounded-full text-[10px] border ${mode.cls}`} title={`Sourcing mode: ${o.binding_mode}`}>{mode.label}</span>
      )}
      {o.binding_gate_age > 0 && (
        <span className="px-1.5 py-px rounded-full text-[10px] border border-red-300 text-red-700 bg-red-50 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800" title={`${gateLabel(o.binding_gate)} open ${o.binding_gate_open_days}d vs ${o.binding_gate_std}d standard`}>gate +{o.binding_gate_age}d</span>
      )}
      {o.any_missing_approval && (
        <span className="px-1.5 py-px rounded-full text-[10px] border border-red-400 text-red-700 bg-red-50 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800" title="Routing has no APPROVAL operation — the approval cycle can't be aged (Control 2 defect)">no APPROVAL op</span>
      )}
      {o.alert_frame_overdue && (
        <span className="px-1.5 py-px rounded-full text-[10px] border border-amber-300 text-amber-700 bg-amber-50 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800">chase vendor</span>
      )}
      {o.any_range && (
        <span className="px-1.5 py-px rounded-full text-[10px] italic border border-gray-300 text-gray-500 dark:border-gray-600 dark:text-gray-400">est · low-confidence</span>
      )}
      {o.job_count > 1 && (
        <span className="px-1.5 py-px rounded-full text-[10px] border border-blue-200 text-blue-600 bg-blue-50 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800">{o.job_count} jobs</span>
      )}
    </div>
  )
}

function TodayRow({ o, bandMap, onOpen }) {
  const buf = Math.round(o.order_runway_p50)
  const eloss = Math.round(elossOf(o, bandMap))
  return (
    <div
      className="grid grid-cols-[minmax(180px,1fr)_240px_66px_74px_86px] gap-x-3 items-center px-1.5 py-2.5 border-t border-gray-100 dark:border-gray-800 first:border-t-0 hover:bg-gray-50 dark:hover:bg-gray-800/40 cursor-pointer"
      onClick={() => onOpen(o.order_num)}
    >
      <div className="min-w-0">
        <div className="text-[13px] font-semibold text-gray-900 dark:text-gray-100">{o.order_num}</div>
        <div className="text-[12.5px] text-gray-800 dark:text-gray-200 truncate" title={o.project_name}>{o.project_name}</div>
        <div className="text-[11.5px] text-gray-500 truncate">{o.customer_name}</div>
        <TodayChips o={o} />
      </div>
      <div><TimelineBar o={o} /></div>
      <div className="text-right tabular-nums">
        <div className="text-xs text-gray-500 dark:text-gray-400">{buf > 0 ? '+' : ''}{buf}d</div>
        <div className="text-[9.5px] text-gray-400">buffer</div>
      </div>
      <div className="text-right tabular-nums">
        <div className="font-bold text-sm text-gray-900 dark:text-gray-100">{o.order_value ? formatCurrency(o.order_value, false) : '—'}</div>
        <div className="text-[9.5px] text-gray-400">order value</div>
      </div>
      <div className="text-right tabular-nums">
        <div className="font-bold text-sm text-red-800 dark:text-red-300">{eloss >= 1 ? formatCurrency(eloss, false) : '—'}</div>
        <div className="text-[9.5px] text-gray-400">exp. $ late</div>
      </div>
    </div>
  )
}

// A blocker sub-queue inside a faceted bucket (rows arrive pre-sorted by expected loss).
function TodayFacet({ facet, rows, bandMap, onOpen }) {
  const [all, setAll] = useState(false)
  const initial = 6
  if (!rows.length) return null
  const shown = all ? rows : rows.slice(0, initial)
  const value = rows.reduce((s, r) => s + (r.order_value || 0), 0)
  return (
    <div className="mt-3">
      <div className="flex items-baseline gap-2 border-t border-gray-100 dark:border-gray-800 pt-2">
        <span className="text-[13px] font-semibold text-gray-700 dark:text-gray-200">{facet.label}</span>
        <span className="text-[11px] text-gray-400"><b className="text-gray-500 dark:text-gray-400">{rows.length}</b> · {formatCurrency(value, false)}</span>
      </div>
      <p className="text-[11.5px] text-gray-400 mt-0.5 mb-1 max-w-3xl">{facet.blurb}</p>
      <div className="overflow-x-auto"><div className="min-w-[720px]">
        {shown.map((o) => <TodayRow key={o.order_num} o={o} bandMap={bandMap} onOpen={onOpen} />)}
      </div></div>
      {rows.length > initial && (
        <button type="button" onClick={() => setAll((v) => !v)} className="mt-1 text-[12px] text-amtrend-blue hover:underline">
          {all ? '▾ show fewer' : `▸ + ${rows.length - initial} more`}
        </button>
      )}
    </div>
  )
}

function TodayBucket({ meta, rows, bandMap, onOpen, collapsed, onToggleCollapse }) {
  const [all, setAll] = useState(false)
  const shown = all ? rows : rows.slice(0, meta.initial)
  const rest = rows.length - shown.length
  const dollars = rows.reduce((s, r) => s + (r.order_value || 0), 0)
  const eloss = rows.reduce((s, r) => s + elossOf(r, bandMap), 0)
  if (!rows.length) return null
  const faceted = typeof meta.facetFn === 'function'
  return (
    <section className={`rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-surface-dark ${BUCKET_BORDER[meta.tone]} p-4`}>
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <button
          type="button"
          onClick={onToggleCollapse}
          className="flex items-center gap-2 font-semibold text-[15px] text-gray-900 dark:text-gray-100 hover:text-amtrend-blue"
          title={collapsed ? 'Expand' : 'Collapse'}
        >
          {collapsed ? <ChevronRight className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
          <span className={`w-2.5 h-2.5 rounded-full ${BUCKET_DOT[meta.tone]}`} />{meta.title}
        </button>
        <div className="text-xs text-gray-500 dark:text-gray-400">
          <b className="text-gray-900 dark:text-gray-100">{rows.length}</b> orders · {formatCurrency(dollars, false)} value · <b className="text-gray-900 dark:text-gray-100">{formatCurrency(Math.round(eloss), false)}</b> expected $ late
        </div>
      </div>

      {!collapsed && (
        <>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5 mb-2 max-w-3xl">{meta.blurb}</p>
          {faceted ? (
            meta.facets.map((f) => (
              <TodayFacet key={f.key} facet={f} rows={rows.filter((r) => meta.facetFn(r) === f.key)} bandMap={bandMap} onOpen={onOpen} />
            ))
          ) : (
            <>
              <div className="overflow-x-auto">
                <div className="min-w-[720px]">
                  {shown.map((o) => <TodayRow key={o.order_num} o={o} bandMap={bandMap} onOpen={onOpen} />)}
                </div>
              </div>
              {rows.length > meta.initial && (
                <button
                  type="button"
                  onClick={() => setAll((v) => !v)}
                  className="mt-1 text-[12.5px] text-amtrend-blue hover:underline"
                >
                  {all ? '▾ show fewer' : `▸ + ${rest} more ${meta.title.toLowerCase()} · ${formatCurrency(rows.slice(meta.initial).reduce((s, r) => s + (r.order_value || 0), 0), false)} order value`}
                </button>
              )}
            </>
          )}
        </>
      )}
    </section>
  )
}

// Owner-first lanes: whose move is it. The reframe — a slip isn't automatically ours.
const OWNER_LANE_META = {
  Amtrend:  { title: 'Ours to move', dot: 'bg-amtrend-blue',
              blurb: 'Amtrend owns the open gate — release, expedite, or renegotiate. Sorted into blocker buckets below.' },
  Factory:  { title: 'At the factory', dot: 'bg-amber-500', border: 'border-t-2 border-t-amber-500',
              blurb: 'Awaiting the overseas / subcontract build. Chase the partner or expedite the sailing — for full import the ocean leg is not compressible.' },
  Customer: { title: 'Awaiting the customer', dot: 'bg-purple-500', border: 'border-t-2 border-t-purple-500',
              blurb: 'The open gate is customer approval (G4). Escalate to sales — this hold is not ours to burn.' },
}
const LANE_ORDER = ['Amtrend', 'Factory', 'Customer']

function laneSums(rows, bandMap) {
  return {
    value: rows.reduce((s, r) => s + (r.order_value || 0), 0),
    eloss: rows.reduce((s, r) => s + elossOf(r, bandMap), 0),
  }
}
const sortByLoss = (arr, bandMap) =>
  [...arr].sort((a, b) => (elossOf(b, bandMap) - elossOf(a, bandMap)) || (a.order_runway_p50 - b.order_runway_p50))

// A flat owner lane (Factory / Customer) — orders ranked by expected loss, with a fold.
function OwnerLaneSection({ laneKey, rows, bandMap, onOpen, collapsed, onToggleCollapse }) {
  const [all, setAll] = useState(false)
  if (!rows.length) return null
  const meta = OWNER_LANE_META[laneKey]
  const { value, eloss } = laneSums(rows, bandMap)
  const shown = all ? rows : rows.slice(0, 8)
  return (
    <section className={`rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-surface-dark ${meta.border} p-4`}>
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <button type="button" onClick={onToggleCollapse}
          className="flex items-center gap-2 font-semibold text-[15px] text-gray-900 dark:text-gray-100 hover:text-amtrend-blue">
          {collapsed ? <ChevronRight className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
          <span className={`w-2.5 h-2.5 rounded-full ${meta.dot}`} />{meta.title}
        </button>
        <div className="text-xs text-gray-500 dark:text-gray-400">
          <b className="text-gray-900 dark:text-gray-100">{rows.length}</b> orders · {formatCurrency(value, false)} value · <b className="text-gray-900 dark:text-gray-100">{formatCurrency(Math.round(eloss), false)}</b> expected $ late
        </div>
      </div>
      {!collapsed && (
        <>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5 mb-2 max-w-3xl">{meta.blurb}</p>
          <div className="overflow-x-auto"><div className="min-w-[720px]">
            {shown.map((o) => <TodayRow key={o.order_num} o={o} bandMap={bandMap} onOpen={onOpen} />)}
          </div></div>
          {rows.length > 8 && (
            <button type="button" onClick={() => setAll((v) => !v)} className="mt-1 text-[12.5px] text-amtrend-blue hover:underline">
              {all ? '▾ show fewer' : `▸ + ${rows.length - 8} more`}
            </button>
          )}
        </>
      )}
    </section>
  )
}

function TodayView({ orders, bandMap, onOpen }) {
  const [collapsed, setCollapsed] = useState(() => new Set())
  const toggle = (k) => setCollapsed((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n })

  const lanes = useMemo(() => {
    const g = { Amtrend: [], Factory: [], Customer: [] }
    for (const o of orders) (g[o.binding_owner] || g.Amtrend).push(o)
    return g
  }, [orders])

  // Ours lane → blocker buckets (the existing Today logic, over Amtrend-owned orders only)
  const oursGroups = useMemo(() => {
    const g = { renegotiate: [], release: [], expedite: [], onplan: [] }
    for (const o of lanes.Amtrend) g[todayBucket(o)].push(o)
    for (const k of Object.keys(g)) g[k].sort((a, b) => (elossOf(b, bandMap) - elossOf(a, bandMap)) || (a.order_runway_p50 - b.order_runway_p50))
    return g
  }, [lanes, bandMap])

  const factory  = useMemo(() => sortByLoss(lanes.Factory, bandMap), [lanes, bandMap])
  const customer = useMemo(() => sortByLoss(lanes.Customer, bandMap), [lanes, bandMap])

  const neg = orders.filter((o) => o.order_runway_p50 < 0).length
  const expLate = orders.reduce((s, o) => s + bandRate(o.risk_band, bandMap) * (MODE_MULT[o.binding_mode] ?? 1), 0)
  const pctLate = orders.length ? Math.round((expLate / orders.length) * 100) : 0

  if (!orders.length) {
    return <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-8 text-center text-sm text-gray-500">No open orders.</div>
  }
  const oursSums = laneSums(lanes.Amtrend, bandMap)
  const oursCollapsed = collapsed.has('Amtrend')

  return (
    <div className="space-y-4">
      {/* Whose-move-is-it lane strip */}
      <div className="grid grid-cols-3 gap-3">
        {LANE_ORDER.map((k) => {
          const st = laneSums(lanes[k], bandMap)
          const meta = OWNER_LANE_META[k]
          return (
            <div key={k} className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-surface-dark p-3">
              <div className="flex items-center gap-2 text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                <span className={`w-2 h-2 rounded-full ${meta.dot}`} />{meta.title}
              </div>
              <div className="mt-1 text-xl font-bold text-gray-900 dark:text-gray-100">
                {lanes[k].length}<span className="text-xs font-normal text-gray-400"> orders</span>
              </div>
              <div className="text-xs text-gray-500">{formatCurrency(st.value, false)} value</div>
            </div>
          )
        })}
      </div>

      <div className="rounded-lg border border-gray-200 dark:border-gray-700 border-l-4 border-l-gray-300 dark:border-l-gray-600 bg-gray-50/60 dark:bg-gray-800/40 p-3.5 text-[12.5px] text-gray-600 dark:text-gray-300">
        <span className="font-semibold text-gray-900 dark:text-gray-100">Whose move, then how bad.</span>{' '}
        Every order sits in the lane of its <b className="text-gray-900 dark:text-gray-100">binding gate</b> — the worst job that gates the ship.
        <b className="text-gray-900 dark:text-gray-100"> {neg}</b> of {orders.length} show a negative buffer, but that's against an{' '}
        <b className="text-gray-900 dark:text-gray-100">infinite-capacity promise</b>; the calibrated bands put <b className="text-gray-900 dark:text-gray-100">~{pctLate}%</b> as expected-late (≈ the 43% baseline).
        Trust the <b className="text-gray-900 dark:text-gray-100">band and rank</b>, not the raw day count. Bar = today → promise ▲ → P50–P80 window.
      </div>

      {/* Ours lane — blocker buckets */}
      <div>
        <div className="flex items-baseline justify-between gap-3 flex-wrap mb-2">
          <button type="button" onClick={() => toggle('Amtrend')}
            className="flex items-center gap-2 font-semibold text-[15px] text-gray-900 dark:text-gray-100 hover:text-amtrend-blue">
            {oursCollapsed ? <ChevronRight className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
            <span className="w-2.5 h-2.5 rounded-full bg-amtrend-blue" />{OWNER_LANE_META.Amtrend.title}
          </button>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            <b className="text-gray-900 dark:text-gray-100">{lanes.Amtrend.length}</b> orders · {formatCurrency(oursSums.value, false)} value · <b className="text-gray-900 dark:text-gray-100">{formatCurrency(Math.round(oursSums.eloss), false)}</b> expected $ late
          </div>
        </div>
        {/* Bucket breakdown — the lane's 247 split across its blocker buckets, so the header
            count reconciles with the (initially small, top-severity) first bucket at a glance. */}
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-gray-500 dark:text-gray-400 mb-2.5 pl-6">
          {BUCKET_META.map((m) => (
            <button key={m.key} type="button"
              onClick={() => { setCollapsed((s) => { const n = new Set(s); n.delete('Amtrend'); n.delete('b:' + m.key); return n }) }}
              className="inline-flex items-center gap-1 hover:text-amtrend-blue">
              <span className={`w-1.5 h-1.5 rounded-full ${BUCKET_DOT[m.tone]}`} />
              {m.title} <b className="text-gray-700 dark:text-gray-300">{oursGroups[m.key].length}</b>
            </button>
          ))}
        </div>
        {!oursCollapsed && (
          <div className="space-y-4">
            {BUCKET_META.map((m) => (
              <TodayBucket
                key={m.key}
                meta={m}
                rows={oursGroups[m.key]}
                bandMap={bandMap}
                onOpen={onOpen}
                collapsed={collapsed.has('b:' + m.key)}
                onToggleCollapse={() => toggle('b:' + m.key)}
              />
            ))}
          </div>
        )}
      </div>

      <OwnerLaneSection laneKey="Factory" rows={factory} bandMap={bandMap} onOpen={onOpen}
        collapsed={collapsed.has('Factory')} onToggleCollapse={() => toggle('Factory')} />
      <OwnerLaneSection laneKey="Customer" rows={customer} bandMap={bandMap} onOpen={onOpen}
        collapsed={collapsed.has('Customer')} onToggleCollapse={() => toggle('Customer')} />

      <p className="text-[11px] text-gray-400 text-center pt-1">
        Every write action (release, expedite, renegotiate, escalate) routes through Trendy's approval-gated agents — never a click here.
      </p>
    </div>
  )
}

// The exception queues, re-cast as per-order flags + list filters (job-keyed ones map
// to their order via the runway job list). gradec is product-level, not order-level, so
// it stays out of the order flow (reachable via the Booking-discipline link).
const EXC_META = [
  { key: 'control1', label: 'No drawing' },
  { key: 'com',      label: 'COM outstanding' },
  { key: 'stalled',  label: 'Stalled' },
  { key: 'unbuilt',  label: 'Not building' },
  { key: 'drafting', label: 'Drafting' },
  { key: 'partgate', label: 'Frame gate' },
  { key: 'approval', label: 'Approval loop' },
  { key: 'exiting',  label: 'Exiting vendor' },
]
const OWNER_DOT = { Amtrend: 'bg-amtrend-blue', Factory: 'bg-amber-500', Customer: 'bg-purple-500' }

// Plain-language gate names — the G1–G8 codes are internal jargon, never shown.
const GATE_LABEL = {
  'G2 Engineer': 'Engineering',
  'G3 Submit': 'Submit drawings',
  'G4 Approve': 'Awaiting approval',
  'G7 Build': 'In fabrication',
  'G8 Deliver': 'In transit / QC',
}
const gateLabel = (g) => (g ? (GATE_LABEL[g] || g) : '—')

// Alert flags read straight off the order row (no exception fetch), rendered as chips
// alongside EXC_META so all filtering is one in-place mechanism (replaces the old
// jump-to-Runway summary cards).
const SYNTH_CHIPS = [
  { key: 'overdue',   label: 'Overdue',    pred: (o) => o.any_overdue },
  { key: 'gate45',    label: 'Gate <45d',  pred: (o) => o.alert_gate_low_runway },
  { key: 'norelease', label: 'No RELEASE', pred: (o) => o.alert_no_release_14d },
  { key: 'aged',      label: 'Aged gate',  pred: (o) => o.binding_gate_age > 0 },
]
const SYNTH_PRED = Object.fromEntries(SYNTH_CHIPS.map((c) => [c.key, c.pred]))

function DrawerMetric({ label, children }) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-2.5">
      <div className="text-[10px] uppercase tracking-wide text-gray-400">{label}</div>
      <div className="mt-0.5 text-sm font-semibold text-gray-900 dark:text-gray-100">{children}</div>
    </div>
  )
}

function DrawerJobsTable({ jobs, bandMap }) {
  if (!jobs || !jobs.length) return <div className="text-xs text-gray-400 py-1">No job detail loaded for this order.</div>
  return (
    <table className="min-w-full text-xs">
      <thead className="text-gray-400"><tr>
        {['Job', 'Cust part', 'Gate / owner', 'Risk', 'Basis', 'Alert'].map((h) => (
          <th key={h} className="text-left font-medium py-1 pr-3 whitespace-nowrap">{h}</th>
        ))}
      </tr></thead>
      <tbody>
        {jobs.map((j) => (
          <tr key={j.job_num} className="border-t border-gray-100 dark:border-gray-800">
            <td className="py-1.5 pr-3 font-mono">{j.job_num}</td>
            <td className="py-1.5 pr-3">{j.x_part_num || j.part_num || '—'}</td>
            <td className="py-1.5 pr-3 whitespace-nowrap">
              {j.open_gate
                ? <span className="inline-flex items-center gap-1"><span className={`w-1.5 h-1.5 rounded-full ${OWNER_DOT[j.owner] || 'bg-gray-400'}`} />{gateLabel(j.open_gate)}</span>
                : '—'}
            </td>
            <td className="py-1.5 pr-3"><RiskBand bandKey={j.risk_band} bandMap={bandMap} p50={j.runway_p50} p80={j.runway_p80_conservative} lowConf={j.render_as_range} /></td>
            <td className="py-1.5 pr-3 text-gray-500">{basisLabel(j.remaining_basis)}</td>
            <td className="py-1.5 pr-3"><AlertBadges gate={j.alert_gate_low_runway} norel={j.alert_no_release_14d} frame={j.alert_frame_overdue} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

// Right-side dossier: everything about one order in a single place — header, flags
// (its own exception queues), jobs with gate/owner, and COM detail. Replaces tab-hopping.
function OrderDrawer({ order, jobs, flags, bandMap, onClose, onOpenRunway, onPrev, onNext, hasPrev, hasNext, position }) {
  useEffect(() => {
    const h = (e) => {
      if (e.key === 'Escape') onClose()
      else if (e.key === 'ArrowRight' && hasNext) onNext()
      else if (e.key === 'ArrowLeft' && hasPrev) onPrev()
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [onClose, onNext, onPrev, hasNext, hasPrev])
  if (!order) return null
  const o = order
  const mode = MODE_BADGE[o.binding_mode]
  const com = flags.find((f) => f.key === 'com')
  const navBtn = 'p-1 rounded border border-gray-200 dark:border-gray-700 text-gray-500 hover:text-amtrend-blue hover:border-amtrend-blue disabled:opacity-30 disabled:hover:text-gray-500 disabled:hover:border-gray-200 dark:disabled:hover:border-gray-700'
  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />
      <aside className="fixed top-0 right-0 h-full w-full sm:max-w-2xl lg:max-w-3xl bg-white dark:bg-surface-dark shadow-2xl z-50 overflow-y-auto border-l border-gray-200 dark:border-gray-700">
        <div className="sticky top-0 bg-white dark:bg-surface-dark border-b border-gray-200 dark:border-gray-700 px-6 py-3 z-10">
          <div className="flex items-center justify-between gap-3">
            {/* Record stepper — arrow through the current view without closing the drawer */}
            <div className="flex items-center gap-1.5">
              <button onClick={onPrev} disabled={!hasPrev} title="Previous (←)" className={navBtn}><ChevronLeft className="w-4 h-4" /></button>
              <button onClick={onNext} disabled={!hasNext} title="Next (→)" className={navBtn}><ChevronRight className="w-4 h-4" /></button>
              {position && <span className="text-[11px] text-gray-400 tabular-nums">{position.n} / {position.total}</span>}
            </div>
            <button onClick={onClose} title="Close (Esc)" className="text-gray-400 hover:text-red-500 shrink-0"><X className="w-5 h-5" /></button>
          </div>
          <div className="min-w-0 mt-1.5">
            <div className="text-xs text-gray-400">Order {o.order_num}</div>
            <div className="font-semibold text-gray-900 dark:text-gray-100 truncate">{o.project_name}</div>
            <div className="text-xs text-gray-500 truncate">{o.customer_name}</div>
          </div>
        </div>

        <div className="px-6 py-5 space-y-5">
          <div className="flex flex-wrap gap-2 items-center text-xs">
            {mode && <span className={`px-2 py-0.5 rounded-full border ${mode.cls}`}>{mode.label}</span>}
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-gray-200 dark:border-gray-700">
              <span className={`w-2 h-2 rounded-full ${OWNER_DOT[o.binding_owner] || 'bg-gray-400'}`} />{o.binding_owner || '—'} owns
            </span>
            <span className="px-2 py-0.5 rounded-full border border-gray-200 dark:border-gray-700">
              {gateLabel(o.binding_gate)}
            </span>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <DrawerMetric label="Buffer"><RiskBand bandKey={o.risk_band} bandMap={bandMap} p50={o.order_runway_p50} p80={o.order_runway_p80} lowConf={o.any_range} /></DrawerMetric>
            <DrawerMetric label="Promise">{formatDate(o.promise_date)}</DrawerMetric>
            <DrawerMetric label="Order value">{o.order_value ? formatCurrency(o.order_value, false) : '—'}</DrawerMetric>
            <DrawerMetric label="Expected $ late">{formatCurrency(Math.round(elossOf(o, bandMap)), false)}</DrawerMetric>
            <DrawerMetric label="Days at gate">
              {o.binding_gate_open_days != null ? `${o.binding_gate_open_days}d` : '—'}
              {o.binding_gate_age > 0 && <span className="text-[11px] font-normal text-red-600 dark:text-red-400"> · +{o.binding_gate_age}d over</span>}
            </DrawerMetric>
            <DrawerMetric label="Jobs">
              {o.job_count ?? jobs?.length ?? '—'}
              {(o.factory_jobs || o.customer_jobs) ? (
                <span className="text-[11px] font-normal text-gray-400"> · {o.factory_jobs || 0} factory · {o.customer_jobs || 0} cust</span>
              ) : null}
            </DrawerMetric>
          </div>

          {(flags.length > 0 || o.any_missing_approval) && (
            <div>
              <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">Flags on this order</div>
              <div className="flex flex-wrap gap-1.5">
                {flags.map((f) => (
                  <span key={f.key} className="px-2 py-0.5 rounded-full text-[11px] bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800">
                    {f.label}{f.count > 1 ? ` ×${f.count}` : ''}
                  </span>
                ))}
                {o.any_missing_approval && (
                  <span className="px-2 py-0.5 rounded-full text-[11px] bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800" title="Routing has no APPROVAL operation (Control 2 defect)">no APPROVAL op</span>
                )}
              </div>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="text-[11px] uppercase tracking-wide text-gray-400">Jobs ({jobs?.length || 0})</div>
              <button onClick={() => onOpenRunway(o.order_num)} className="text-[11px] text-amtrend-blue hover:underline">Open in Runway ↗</button>
            </div>
            <div className="overflow-x-auto"><DrawerJobsTable jobs={jobs} bandMap={bandMap} /></div>
          </div>

          {com && (
            <div>
              <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">COM outstanding ({com.count})</div>
              <table className="min-w-full text-xs">
                <thead className="text-gray-400"><tr>{['PO', 'Part', 'Vendor', 'Age'].map((h) => <th key={h} className="text-left font-medium py-1 pr-3">{h}</th>)}</tr></thead>
                <tbody>
                  {com.rows.slice(0, 12).map((r, i) => (
                    <tr key={i} className="border-t border-gray-100 dark:border-gray-800">
                      <td className="py-1 pr-3 font-mono">{r.po_num}-{r.po_line}</td>
                      <td className="py-1 pr-3">{r.part_num}</td>
                      <td className="py-1 pr-3 text-gray-500">{r.vendor_name}</td>
                      <td className={`py-1 pr-3 ${r.age_days > 180 ? 'text-red-600 dark:text-red-400 font-semibold' : ''}`}>{r.age_days}d</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </aside>
    </>
  )
}

// The user guide — the whole operating model in one drawer, kept current with the UI.
function GuideDrawer({ bands, onClose }) {
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [onClose])
  const H = ({ children }) => <div className="text-[11px] uppercase tracking-wide text-gray-400 mt-5 mb-1.5">{children}</div>
  const modeChip = (k) => { const m = MODE_BADGE[k]; return <span className={`px-1.5 py-px rounded-full text-[10px] border ${m.cls}`}>{m.label}</span> }
  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />
      <aside className="fixed top-0 right-0 h-full w-full sm:max-w-lg bg-white dark:bg-surface-dark shadow-2xl z-50 overflow-y-auto border-l border-gray-200 dark:border-gray-700">
        <div className="sticky top-0 bg-white dark:bg-surface-dark border-b border-gray-200 dark:border-gray-700 px-5 py-3 flex items-center justify-between z-10">
          <div className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2"><BookOpen className="w-4 h-4 text-amtrend-blue" /> How the monitor works</div>
          <button onClick={onClose} title="Close (Esc)" className="text-gray-400 hover:text-red-500"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-5 py-4 text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
          <p>The board lists <b className="text-gray-900 dark:text-gray-100">open orders</b>, grouped by <b className="text-gray-900 dark:text-gray-100">whose move it is</b>, then ranked by how at-risk they are. Click any row to open its full <b>dossier</b> — jobs, flags, gate, and COM in one panel.</p>

          <H>The three lanes — whose move</H>
          <ul className="space-y-1.5 list-none pl-0">
            <li><span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amtrend-blue" /><b className="text-gray-900 dark:text-gray-100">Ours to move</b></span> — Amtrend owns the open step. Release, expedite, or renegotiate. Split into blocker buckets.</li>
            <li><span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" /><b className="text-gray-900 dark:text-gray-100">At the factory</b></span> — waiting on the overseas / subcontract build. Chase the partner; for imports the ocean transit can't be sped up.</li>
            <li><span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-purple-500" /><b className="text-gray-900 dark:text-gray-100">Awaiting the customer</b></span> — the open step is customer approval. Escalate to sales — this hold isn't ours.</li>
          </ul>

          <H>Reading a row</H>
          <p className="mb-2"><b className="text-gray-900 dark:text-gray-100">Mode</b> — how it's built, so you know if a slip is recoverable: {modeChip('domestic')} in-house · {modeChip('nearshore_mx')} truck from Mexico · {modeChip('full_import')} fixed ocean sailing (not recoverable) · {modeChip('hybrid')} frame imported, built here.</p>
          <p className="mb-2"><b className="text-gray-900 dark:text-gray-100">Gate</b> — the step it's stuck at: Engineering → Submit drawings → Awaiting approval → In fabrication → In transit / QC.</p>
          <p className="mb-2"><b className="text-gray-900 dark:text-gray-100">Buffer</b> — the customer's promised date minus how long we still expect the job to take. It's a calibrated <b>risk rank</b>, not a literal day count (bands below). Import risk is weighted up — a positive buffer doesn't protect a fixed sailing.</p>
          <p><b className="text-gray-900 dark:text-gray-100">Age</b> — how long the current gate has been open vs its standard. Catches a stuck step the buffer can hide (an order can look on-plan while sitting 40 days in approval).</p>

          <H>Risk bands</H>
          <RiskLegend bands={bands} />

          <H>Flag chips</H>
          <p>Click a chip (COM outstanding, Stalled, Not building, Aged gate, Overdue, …) to slice the list in place to the orders carrying that flag.</p>

          <H>The tabs</H>
          <ul className="space-y-1 list-disc pl-4">
            <li><b className="text-gray-900 dark:text-gray-100">Today</b> — the owner lanes (the daily worklist).</li>
            <li><b className="text-gray-900 dark:text-gray-100">Runway</b> — every open order as a flat table.</li>
            <li><b className="text-gray-900 dark:text-gray-100">Booking discipline</b> — where fitted standards are shaky (data quality).</li>
            <li><b className="text-gray-900 dark:text-gray-100">Missing drawing</b> — custom orders released to fabrication with no approved drawing (PartDraw) on file. Program SKUs are excluded.</li>
            <li><b className="text-gray-900 dark:text-gray-100">Routing defects</b> — jobs whose routing has no approval step, so approvals can't be aged.</li>
            <li><b className="text-gray-900 dark:text-gray-100">Why this works</b> — the backtest: buffer at the gate predicts lateness.</li>
          </ul>

          <H>Trust it, honestly</H>
          <p>Most open orders show a negative buffer, but that's measured against an <b>infinite-capacity promise</b> — the calibrated bands put ~half as actually-late (≈ the 43% baseline). Trust the band and the rank, not the raw day count. The snapshot is a point-in-time Epicor copy; the pill up top shows its age. Nothing here writes back to Epicor — every action routes through Trendy's approval-gated agents.</p>
        </div>
      </aside>
    </>
  )
}

// Free-text order match: order #, project, or customer (case-insensitive substring).
function orderMatches(o, q) {
  if (!q) return true
  const s = q.trim().toLowerCase()
  if (!s) return true
  return String(o.order_num).includes(s)
    || (o.project_name || '').toLowerCase().includes(s)
    || (o.customer_name || '').toLowerCase().includes(s)
}

// Snapshot freshness → { label, stale }. The snapshot is a point-in-time Epicor
// copy, not live, so surfacing its age keeps the team from acting on stale data.
function snapshotAge(iso) {
  if (!iso) return null
  const then = new Date(iso)
  const ms = Date.now() - then.getTime()
  const hrs = ms / 3.6e6
  const days = Math.floor(hrs / 24)
  const rel = hrs < 1 ? 'under an hour ago'
    : hrs < 24 ? `${Math.round(hrs)}h ago`
    : `${days} day${days === 1 ? '' : 's'} ago`
  const when = then.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  return { label: `Snapshot ${when} · ${rel}`, stale: hrs >= 24 }
}

export default function ProductionFlow() {
  const [tab, setTab] = useState('today')
  const [summary, setSummary] = useState(null)
  const [runway, setRunway] = useState([])
  const [orders, setOrders] = useState([])
  const [expanded, setExpanded] = useState({})
  const [activeFilter, setActiveFilter] = useState('all')
  const [bands, setBands] = useState([])
  const [exceptions, setExceptions] = useState({})
  const [curve, setCurve] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [scrollTarget, setScrollTarget] = useState(null)  // Today row → scroll that order into view on Runway
  const [flashOrder, setFlashOrder] = useState(null)      // brief highlight of the drilled-into order
  const [query, setQuery] = useState('')                  // free-text search across order/project/customer
  const [guideOpen, setGuideOpen] = useState(false)   // user-guide drawer
  const [drawerOrder, setDrawerOrder] = useState(null)     // order # whose dossier drawer is open
  const [excFilter, setExcFilter] = useState(null)         // active exception filter chip (or null)

  useEffect(() => {
    let alive = true
    ;(async () => {
      setLoading(true)
      setError(null)
      try {
        const [s, r, ord, cm, st, gc, ex, pg, ap, ub, dr, lc, rb, mr, ma, c1] = await Promise.all([
          getRunwaySummary(),
          getRunwayList({ limit: 2000 }),
          getRunwayOrders({ limit: 1000 }),
          getRunwayExceptions('com'),
          getRunwayExceptions('stalled'),
          getRunwayExceptions('gradec'),
          getRunwayExceptions('exiting'),
          getRunwayExceptions('partgate'),
          getRunwayExceptions('approval'),
          getRunwayExceptions('unbuilt'),
          getRunwayExceptions('drafting'),
          getRunwayLateCurve(),
          getRiskBands(),
          getModeRisk().catch(() => null),
          getRunwayExceptions('missing_approval').catch(() => []),
          getRunwayExceptions('control1').catch(() => []),
        ])
        if (!alive) return
        if (mr && mr.length) MODE_MULT = Object.fromEntries(mr.map((m) => [m.mode, Number(m.late_multiplier)]))
        setSummary(s)
        setRunway(r || [])
        setOrders(ord || [])
        setExceptions({ com: cm || [], stalled: st || [], gradec: gc || [], exiting: ex || [], partgate: pg || [], approval: ap || [], unbuilt: ub || [], drafting: dr || [], missing_approval: ma || [], control1: c1 || [] })
        setCurve(lc || [])
        setBands(rb || [])
      } catch (e) {
        if (alive) setError(e.status === 503
          ? 'Runway data source not configured yet (set PRODUCTION_FLOW_DATABASE_URL on the backend).'
          : (e.message || 'Failed to load runway data.'))
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => { alive = false }
  }, [])

  const jobsByOrder = useMemo(() => {
    const m = {}
    for (const j of runway) { (m[j.order_num] = m[j.order_num] || []).push(j) }
    return m
  }, [runway])

  const bandMap = useMemo(
    () => Object.fromEntries((bands || []).map((b) => [b.band_key, b])),
    [bands],
  )

  // Total open-order value (open-line qty×price) — the revenue-scale headline the
  // labor-$ figure alone was missing. Summed client-side from the order rollup.
  const orderValueTotal = useMemo(
    () => orders.reduce((sum, o) => sum + (o.order_value || 0), 0),
    [orders],
  )

  const ordersById = useMemo(
    () => Object.fromEntries(orders.map((o) => [o.order_num, o])),
    [orders],
  )
  // Map each exception queue's rows to their order (job-keyed rows via the runway list),
  // so exceptions become per-order flags + list filters instead of 9 separate tabs.
  const job2order = useMemo(() => {
    const m = {}
    for (const j of runway) m[j.job_num] = j.order_num
    return m
  }, [runway])
  const excRowsByOrder = useMemo(() => {
    const out = {}
    for (const { key } of EXC_META) {
      const byOrder = {}
      for (const r of (exceptions[key] || [])) {
        const ord = r.order_num ?? job2order[r.job_num]
        if (ord == null) continue
        ;(byOrder[ord] = byOrder[ord] || []).push(r)
      }
      out[key] = byOrder
    }
    return out
  }, [exceptions, job2order])
  const excOrderSets = useMemo(() => {
    const out = {}
    for (const { key } of EXC_META) out[key] = new Set(Object.keys(excRowsByOrder[key] || {}).map(Number))
    return out
  }, [excRowsByOrder])
  const drawerFlags = useMemo(() => {
    if (drawerOrder == null) return []
    const out = []
    for (const { key, label } of EXC_META) {
      const rows = excRowsByOrder[key]?.[drawerOrder] || []
      if (rows.length) out.push({ key, label, count: rows.length, rows })
    }
    return out
  }, [drawerOrder, excRowsByOrder])

  // Clicking a summary card sets a filter and jumps to the Runway tab.
  const applyFilter = (key) => {
    setActiveFilter((cur) => (cur === key ? 'all' : key))
    setTab('runway')
  }

  // Clicking any order row opens its dossier drawer (the unified detail surface).
  const openOrder = (orderNum) => setDrawerOrder(orderNum)

  // From the drawer: jump into the full Runway list with the order scrolled + flashed.
  const openInRunway = (orderNum) => {
    setDrawerOrder(null)
    setActiveFilter('all')
    setExpanded((p) => ({ ...p, [orderNum]: true }))
    setTab('runway')
    setScrollTarget(orderNum)
  }

  // After a Today-row drill-through lands on the Runway tab, scroll the target
  // order into view (it may be far down the buffer-sorted list) and flash it.
  useEffect(() => {
    if (tab !== 'runway' || !scrollTarget) return
    const id = scrollTarget
    setScrollTarget(null)
    setFlashOrder(id)
    const raf = requestAnimationFrame(() =>
      document.getElementById(`ord-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    )
    const t = setTimeout(() => setFlashOrder(null), 1800)
    return () => { cancelAnimationFrame(raf); clearTimeout(t) }
  }, [tab, scrollTarget])

  // Filter the order rollup (and, when a filter is active, the jobs inside each
  // order) down to rows matching the selected card's predicate.
  const excMatch = (o) => {
    if (!excFilter) return true
    if (SYNTH_PRED[excFilter]) return !!SYNTH_PRED[excFilter](o)   // Overdue / Gate<45 / No RELEASE / Aged gate
    return excOrderSets[excFilter]?.has(o.order_num) ?? false
  }

  const filteredOrders = useMemo(() => {
    const f = ORDER_FILTERS[activeFilter] || ORDER_FILTERS.all
    return orders.filter((o) => f.order(o) && orderMatches(o, query) && excMatch(o))
  }, [orders, activeFilter, query, excFilter, excOrderSets])

  // Today respects search + the exception chip filter (not the Runway card filters).
  const todayOrders = useMemo(
    () => orders.filter((o) => orderMatches(o, query) && excMatch(o)),
    [orders, query, excFilter, excOrderSets],
  )

  // Ordered list of order #s the drawer stepper walks — matches whatever the active
  // tab is showing, so ←/→ (and the header arrows) move through the visible records.
  const drawerList = useMemo(() => {
    let src
    if (tab === 'today') {
      const rank = { Amtrend: 0, Factory: 1, Customer: 2 }
      src = [...todayOrders].sort((a, b) =>
        (rank[a.binding_owner] ?? 0) - (rank[b.binding_owner] ?? 0) ||
        (elossOf(b, bandMap) - elossOf(a, bandMap)))
    } else if (tab === 'control1') src = exceptions.control1 || []
    else if (tab === 'missing_approval') src = exceptions.missing_approval || []
    else if (tab === 'gradec') src = exceptions.gradec || []
    else src = filteredOrders
    const seen = new Set(); const out = []
    for (const r of src) { const on = r.order_num; if (on != null && !seen.has(on)) { seen.add(on); out.push(on) } }
    return out
  }, [tab, todayOrders, filteredOrders, exceptions, bandMap])
  const drawerIdx = drawerOrder == null ? -1 : drawerList.indexOf(drawerOrder)
  const gotoDrawer = (i) => { if (i >= 0 && i < drawerList.length) setDrawerOrder(drawerList[i]) }

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amtrend-blue" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 p-4 text-amber-800 dark:text-amber-300">
          <div className="flex items-center gap-2 font-medium"><AlertTriangle className="w-5 h-5" /> Runway Monitor</div>
          <p className="mt-1 text-sm">{error}</p>
        </div>
      </div>
    )
  }

  const s = summary || {}
  const alertsActive = (s.alert_gate_low_runway || 0) + (s.alert_no_release || 0)

  return (
    <div className="p-6 space-y-6 max-w-[1400px] mx-auto">
      <div>
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-amtrend-blue mb-2">
          <ArrowLeft className="w-4 h-4" /> Back to Trendy
        </Link>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Gauge className="w-6 h-6 text-amtrend-blue" /> Runway Monitor
          </h1>
          {(() => {
            const age = snapshotAge(s.snapshot_at)
            if (!age) return null
            return (
              <span
                title={age.stale ? 'This is a point-in-time snapshot, not live Epicor data — it may be out of date.' : 'Snapshot freshness'}
                className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${
                  age.stale
                    ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
                    : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300'
                }`}
              >
                <Clock className="w-3.5 h-3.5" />{age.label}{age.stale ? ' — stale' : ''}
              </span>
            )
          })()}
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2 flex-wrap">
          <span>Open orders by whose move it is, then how at-risk. Click a row to open its dossier.</span>
          <button
            type="button"
            onClick={() => setGuideOpen(true)}
            className="inline-flex items-center gap-1 text-amtrend-blue hover:underline"
          >
            <BookOpen className="w-3.5 h-3.5" /> Guide
          </button>
        </p>
      </div>

      {/* Scale headline — grand totals not shown per-lane */}
      <div className="flex flex-wrap items-baseline gap-x-5 gap-y-1 text-sm">
        <span className="text-gray-500 dark:text-gray-400"><b className="text-amtrend-blue text-lg">{formatCurrency(orderValueTotal, false)}</b> open order value</span>
        <span className="text-gray-500 dark:text-gray-400"><b className="text-gray-900 dark:text-gray-100 text-lg">{formatCurrency(s.total_dollars_at_risk, false)}</b> labor at risk</span>
        <span className="text-gray-400">{s.open_customer_jobs ?? '—'} open jobs</span>
      </div>

      {/* Tabs — Today · Runway · Booking discipline · Why this works.
          The old Exceptions dropdown is gone: those queues are now per-order flags
          (in the drawer) + the chip filter bar below. gradec stays as its own view. */}
      <div className="border-b border-gray-200 dark:border-gray-700 flex items-center gap-1">
        <button onClick={() => setTab('today')} className={tabBtnCls(tab === 'today')}>
          <ListChecks className="w-4 h-4" /> Today <TabCount n={orders.length || null} active={tab === 'today'} />
        </button>
        <button onClick={() => setTab('runway')} className={tabBtnCls(tab === 'runway')}>
          <Gauge className="w-4 h-4" /> Runway <TabCount n={orders.length || null} active={tab === 'runway'} />
        </button>
        <button onClick={() => setTab('gradec')} className={tabBtnCls(tab === 'gradec')}>
          <TrendingDown className="w-4 h-4" /> Booking discipline <TabCount n={exceptions.gradec?.length || null} active={tab === 'gradec'} />
        </button>
        <button onClick={() => setTab('control1')} className={tabBtnCls(tab === 'control1')}>
          <FileWarning className="w-4 h-4" /> Missing drawing <TabCount n={exceptions.control1?.length || null} active={tab === 'control1'} />
        </button>
        <button onClick={() => setTab('missing_approval')} className={tabBtnCls(tab === 'missing_approval')}>
          <FileCheck2 className="w-4 h-4" /> Routing defects <TabCount n={exceptions.missing_approval?.length || null} active={tab === 'missing_approval'} />
        </button>
        <button onClick={() => setTab('validation')} className={tabBtnCls(tab === 'validation')}>
          <AlertTriangle className="w-4 h-4" /> Why this works
        </button>
      </div>

      {(tab === 'today' || tab === 'runway') && (
        <div className="flex items-center gap-2">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search project, customer, or order #…"
              className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-surface-dark pl-9 pr-9 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-amtrend-blue focus:border-amtrend-blue"
            />
            {query && (
              <button type="button" onClick={() => setQuery('')} title="Clear search"
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {query && (
            <span className="text-xs text-gray-400">
              {(tab === 'today' ? todayOrders : filteredOrders).length} match{(tab === 'today' ? todayOrders : filteredOrders).length === 1 ? '' : 'es'}
            </span>
          )}
        </div>
      )}

      {/* Exception filter chips — click to slice the list to orders with that flag */}
      {(tab === 'today' || tab === 'runway') && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] uppercase tracking-wide text-gray-400 mr-1">Flags</span>
          {EXC_META.map(({ key, label }) => {
            const n = excOrderSets[key]?.size || 0
            if (!n) return null
            const on = excFilter === key
            return (
              <button
                key={key}
                type="button"
                onClick={() => setExcFilter(on ? null : key)}
                className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                  on
                    ? 'bg-amtrend-blue text-white border-amtrend-blue'
                    : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-amtrend-blue'
                }`}
              >
                {label} <span className={on ? 'opacity-80' : 'text-gray-400'}>{n}</span>
              </button>
            )
          })}
          {SYNTH_CHIPS.map(({ key, label, pred }) => {
            const n = orders.filter(pred).length
            if (!n) return null
            const on = excFilter === key
            return (
              <button
                key={key}
                type="button"
                onClick={() => setExcFilter(on ? null : key)}
                className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                  on ? 'bg-amtrend-blue text-white border-amtrend-blue' : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-amtrend-blue'
                }`}
              >
                {label} <span className={on ? 'opacity-80' : 'text-gray-400'}>{n}</span>
              </button>
            )
          })}
          {excFilter && (
            <button type="button" onClick={() => setExcFilter(null)} className="text-xs text-gray-400 hover:text-red-500 inline-flex items-center gap-0.5">
              <X className="w-3.5 h-3.5" /> clear
            </button>
          )}
        </div>
      )}

      {tab === 'today' && (
        <TodayView orders={todayOrders} bandMap={bandMap} onOpen={openOrder} />
      )}
      {tab === 'runway' && (
        <>
          {activeFilter !== 'all' && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-500 dark:text-gray-400">Filtered by</span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-amtrend-blue/10 text-amtrend-blue px-2.5 py-0.5 font-medium">
                {ORDER_FILTERS[activeFilter].label}
                <button type="button" onClick={() => setActiveFilter('all')} className="hover:text-red-500" title="Clear filter">
                  <X className="w-3.5 h-3.5" />
                </button>
              </span>
              <span className="text-gray-400">{filteredOrders.length} orders</span>
            </div>
          )}
          <OrderRunwayList
            orders={filteredOrders}
            bandMap={bandMap}
            flashOrder={flashOrder}
            onOpen={openOrder}
          />
        </>
      )}
      {tab === 'com' && <ComTable rows={exceptions.com} />}
      {tab === 'stalled' && <StalledTable rows={exceptions.stalled} />}
      {tab === 'unbuilt' && <UnbuiltTable rows={exceptions.unbuilt} />}
      {tab === 'drafting' && <DraftingTable rows={exceptions.drafting} />}
      {tab === 'gradec' && <GradeCTable rows={exceptions.gradec} />}
      {tab === 'control1' && <Control1Table rows={exceptions.control1} onOpen={openOrder} />}
      {tab === 'missing_approval' && <MissingApprovalTable rows={exceptions.missing_approval} onOpen={openOrder} />}
      {tab === 'exiting' && <ExitingTable rows={exceptions.exiting} />}
      {tab === 'partgate' && <PartGateTable rows={exceptions.partgate} />}
      {tab === 'approval' && <ApprovalTable rows={exceptions.approval} />}
      {tab === 'validation' && <ValidationChart data={curve} />}

      {drawerOrder != null && (
        <OrderDrawer
          order={ordersById[drawerOrder]}
          jobs={jobsByOrder[drawerOrder]}
          flags={drawerFlags}
          bandMap={bandMap}
          onClose={() => setDrawerOrder(null)}
          onOpenRunway={openInRunway}
          onPrev={() => gotoDrawer(drawerIdx - 1)}
          onNext={() => gotoDrawer(drawerIdx + 1)}
          hasPrev={drawerIdx > 0}
          hasNext={drawerIdx >= 0 && drawerIdx < drawerList.length - 1}
          position={drawerIdx >= 0 ? { n: drawerIdx + 1, total: drawerList.length } : null}
        />
      )}
      {guideOpen && <GuideDrawer bands={bands} onClose={() => setGuideOpen(false)} />}
    </div>
  )
}

function Table({ headers, children }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 dark:bg-gray-800/50 text-gray-500 dark:text-gray-400">
          <tr>{headers.map((h) => <th key={h} className="text-left font-medium px-3 py-2 whitespace-nowrap">{h}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">{children}</tbody>
      </table>
    </div>
  )
}

function gradeFromRank(rank) {
  return rank === 3 ? 'A' : rank === 2 ? 'B' : 'C'
}

function AlertBadges({ gate, norel, frame }) {
  return (
    <span className="space-x-1 whitespace-nowrap">
      {gate && <span className="px-1.5 py-0.5 rounded text-xs bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">GATE&lt;45</span>}
      {norel && <span className="px-1.5 py-0.5 rounded text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">NO-RELEASE</span>}
      {frame && <span className="px-1.5 py-0.5 rounded text-xs bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">FRAME OVERDUE</span>}
    </span>
  )
}

// Plain-language "what's wrong / what to do" for an order — the single most severe
// actionable reason, so a reader gets intent without decoding a buffer number.
const REASON_TONE = {
  red: 'text-red-600 dark:text-red-400',
  amber: 'text-amber-600 dark:text-amber-400',
  green: 'text-emerald-600 dark:text-emerald-400',
}
function orderReason(o) {
  if (o.alert_frame_overdue) return { text: 'Frame overdue — chase the vendor', tone: 'red' }
  if (o.alert_no_release_14d) return { text: 'No signed RELEASE — release it', tone: 'amber' }
  if (o.alert_gate_low_runway) return { text: 'Gate cleared < 45d — expedite', tone: 'red' }
  if (o.risk_band === 'critical') return { text: 'Deeply behind — renegotiate date', tone: 'red' }
  if (o.any_overdue) return { text: 'Past promise — renegotiate', tone: 'red' }
  if (o.risk_band === 'at_risk') return { text: 'Behind — expedite or renegotiate', tone: 'amber' }
  return { text: 'On plan (~34% still slip)', tone: 'green' }
}

// Flat order table — clicking a row opens the dossier drawer (jobs/flags live there).
function OrderRunwayList({ orders, onOpen, bandMap, flashOrder }) {
  if (!orders.length) {
    return (
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-8 text-center text-sm text-gray-500 dark:text-gray-400">
        No orders match this filter.
      </div>
    )
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 dark:bg-gray-800/50 text-gray-500 dark:text-gray-400">
          <tr>
            {['Project / Site', 'Mode', 'Owner / gate', 'Jobs', 'Risk (buffer)', 'Promise', 'Order value', 'Why', ''].map((h) => (
              <th key={h} className="text-left font-medium px-3 py-2 whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
          {orders.map((o) => {
            const mode = MODE_BADGE[o.binding_mode]
            return (
              <tr
                key={o.order_num}
                id={`ord-${o.order_num}`}
                className={`hover:bg-gray-50 dark:hover:bg-gray-800/40 cursor-pointer transition-colors ${flashOrder === o.order_num ? 'bg-amtrend-blue/10 ring-2 ring-inset ring-amtrend-blue' : ''}`}
                onClick={() => onOpen(o.order_num)}
              >
                <td className="px-3 py-2">
                  <div className="font-medium text-gray-900 dark:text-gray-100">{o.project_name}</div>
                  <div className="text-xs text-gray-500">{o.customer_name} · order {o.order_num}</div>
                </td>
                <td className="px-3 py-2">{mode && <span className={`px-1.5 py-px rounded-full text-[10px] border ${mode.cls}`}>{mode.label}</span>}</td>
                <td className="px-3 py-2 whitespace-nowrap text-xs">
                  <span className="inline-flex items-center gap-1"><span className={`w-1.5 h-1.5 rounded-full ${OWNER_DOT[o.binding_owner] || 'bg-gray-400'}`} />{o.binding_owner || '—'}</span>
                  <span className="text-gray-400"> · {gateLabel(o.binding_gate)}</span>
                </td>
                <td className="px-3 py-2 text-gray-500">{o.job_count}</td>
                <td className="px-3 py-2">
                  <RiskBand bandKey={o.risk_band} bandMap={bandMap} p50={o.order_runway_p50} p80={o.order_runway_p80} lowConf={o.any_range} />
                </td>
                <td className="px-3 py-2">{formatDate(o.promise_date)}</td>
                <td className="px-3 py-2 font-medium text-gray-900 dark:text-gray-100">{o.order_value ? formatCurrency(o.order_value, false) : '—'}</td>
                <td className="px-3 py-2 text-xs">
                  {(() => { const r = orderReason(o); return <span className={`font-medium ${REASON_TONE[r.tone]}`}>{r.text}</span> })()}
                </td>
                <td className="px-3 py-2 text-gray-300"><ChevronRight className="w-4 h-4" /></td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function ComTable({ rows }) {
  return (
    <Table headers={['Project / Site', 'PO', 'Part', 'Vendor', 'Raised', 'Age (days)', 'Job']}>
      {rows.map((r, i) => (
        <tr key={`${r.po_num}-${r.po_line}-${r.po_rel_num}-${i}`} className="hover:bg-gray-50 dark:hover:bg-gray-800/40">
          <td className="px-3 py-2"><ProjectCell project={r.project_name} customer={r.customer_name} /></td>
          <td className="px-3 py-2 font-mono text-xs">{r.po_num}-{r.po_line}</td>
          <td className="px-3 py-2">{r.part_num}</td>
          <td className="px-3 py-2 text-gray-500">{r.vendor_name}</td>
          <td className="px-3 py-2">{formatDate(r.po_raised)}</td>
          <td className={`px-3 py-2 ${r.age_days > 180 ? 'text-red-600 dark:text-red-400 font-semibold' : ''}`}>{r.age_days}</td>
          <td className="px-3 py-2 font-mono text-xs">{r.job_num || '—'}</td>
        </tr>
      ))}
    </Table>
  )
}

function StalledTable({ rows }) {
  return (
    <Table headers={['Project / Site', 'Job', 'Family', 'Last labor', 'Days idle', 'Gated', 'Labor $']}>
      {rows.map((r) => (
        <tr key={r.job_num} className="hover:bg-gray-50 dark:hover:bg-gray-800/40">
          <td className="px-3 py-2"><ProjectCell project={r.project_name} customer={r.customer_name} /></td>
          <td className="px-3 py-2 font-mono text-xs">{r.job_num}</td>
          <td className="px-3 py-2">{r.family}</td>
          <td className="px-3 py-2">{formatDate(r.last_labor_date)}</td>
          <td className={`px-3 py-2 ${r.days_idle > 90 ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-amber-600 dark:text-amber-400'}`}>{r.days_idle}</td>
          <td className="px-3 py-2">{r.gated ? 'yes' : 'no'}</td>
          <td className="px-3 py-2">{formatCurrency(r.dollars_stalled, false)}</td>
        </tr>
      ))}
    </Table>
  )
}

function BlockerChip({ blocker }) {
  const map = {
    'awaiting frame': 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
    'awaiting COM': 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
    'not started': 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
    'idle': 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
  }
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${map[blocker] || map.idle}`}>{blocker}</span>
}

function UnbuiltTable({ rows }) {
  if (!rows) return null
  const totRemHrs = rows.reduce((s, r) => s + (Number(r.remaining_est_hours) || 0), 0)
  const totDollars = rows.reduce((s, r) => s + (Number(r.dollars_at_risk) || 0), 0)
  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 p-4 text-sm text-gray-600 dark:text-gray-300">
        <span className="font-semibold text-gray-900 dark:text-gray-100">Released to the floor, not building.</span>{' '}
        Jobs past the fabrication (RELEASE) gate that have burned under 5% of their estimated labor —
        they've been released but work hasn't meaningfully begun. Progress is measured on <em>labor hours</em>,
        not completed units, because Amtrend jobs are mostly qty-1 pieces (completed-qty stays 0 until the piece ships).
        <div className="mt-2 text-xs text-gray-500">
          {rows.length} jobs · {Math.round(totRemHrs).toLocaleString()} est hours of work not yet started · {formatCurrency(totDollars, false)} labor exposure
        </div>
      </div>
      <Table headers={['Project / Site', 'Job', 'Family', 'Since gate', 'Est hrs', 'Done', 'Remaining', 'Ops open', 'Last labor', 'Buffer', 'Labor $', 'Blocker']}>
        {rows.map((r) => (
          <tr key={r.job_num} className="hover:bg-gray-50 dark:hover:bg-gray-800/40">
            <td className="px-3 py-2"><ProjectCell project={r.project_name} customer={r.customer_name} /></td>
            <td className="px-3 py-2 font-mono text-xs">{r.job_num}</td>
            <td className="px-3 py-2">{r.family}</td>
            <td className={`px-3 py-2 ${r.days_since_gate > 45 ? 'text-red-600 dark:text-red-400 font-semibold' : r.days_since_gate > 21 ? 'text-amber-600 dark:text-amber-400' : ''}`}>{r.days_since_gate}d</td>
            <td className="px-3 py-2 font-mono text-xs">{r.est_hours}</td>
            <td className="px-3 py-2 font-mono text-xs text-gray-500">{r.act_hours}</td>
            <td className="px-3 py-2 font-mono text-xs font-semibold">{r.remaining_est_hours}</td>
            <td className="px-3 py-2 font-mono text-xs">{r.open_ops}/{r.total_ops}</td>
            <td className="px-3 py-2 text-xs">{r.never_touched ? <span className="text-red-500">never</span> : formatDate(r.last_labor)}</td>
            <td className={`px-3 py-2 font-mono ${r.buffer_days < 0 ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-gray-700 dark:text-gray-300'}`}>{r.buffer_days > 0 ? '+' : ''}{r.buffer_days}</td>
            <td className="px-3 py-2">{formatCurrency(r.dollars_at_risk, false)}</td>
            <td className="px-3 py-2"><BlockerChip blocker={r.blocker} /></td>
          </tr>
        ))}
      </Table>
    </div>
  )
}

function DraftingStatusChip({ status }) {
  const map = {
    received: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
    pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
    'not issued': 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  }
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${map[status] || 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'}`}>{status}</span>
}

function DraftingTable({ rows }) {
  if (!rows) return null
  const pending = rows.filter((r) => r.status === 'pending' || r.status === 'not issued')
  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 p-4 text-sm text-gray-600 dark:text-gray-300">
        <span className="font-semibold text-gray-900 dark:text-gray-100">Outsourced drafting phase.</span>{' '}
        OS-DRAFT is a front-end phase (Studio Q et al.), <em>not</em> part of the fabrication lane — measured from
        the drafting PO issue date to its <em>receipt</em> (receipt = drawings delivered; Epicor's OpComplete flag is
        unreliable here, so we key on the physical receipt). Buffers are unaffected by this view.
        <div className="mt-2 text-xs text-gray-500">
          {rows.length} jobs · {pending.length} still awaiting drawings — these are what hold up RELEASE.
        </div>
      </div>
      <Table headers={['Project / Site', 'Job', 'Drafter', 'PO issued', 'Received', 'Status', 'Days out', 'Turnaround']}>
        {rows.map((r) => (
          <tr key={r.job_num} className="hover:bg-gray-50 dark:hover:bg-gray-800/40">
            <td className="px-3 py-2"><ProjectCell project={r.project_name} customer={r.customer_name} /></td>
            <td className="px-3 py-2 font-mono text-xs">{r.job_num}</td>
            <td className="px-3 py-2">{r.vendor_name || '—'}{r.is_studio_q && <span className="ml-1 text-xs text-amtrend-blue">◆</span>}</td>
            <td className="px-3 py-2">{formatDate(r.po_issue_date)}</td>
            <td className="px-3 py-2">{formatDate(r.receipt_date)}</td>
            <td className="px-3 py-2"><DraftingStatusChip status={r.status} /></td>
            <td className={`px-3 py-2 font-mono ${r.days_out_unreceived > 45 ? 'text-red-600 dark:text-red-400 font-semibold' : r.days_out_unreceived > 21 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-500'}`}>
              {r.days_out_unreceived != null ? `${r.days_out_unreceived}d` : '—'}
            </td>
            <td className="px-3 py-2 font-mono text-xs text-gray-500">{r.drafting_days != null && r.status === 'received' ? `${r.drafting_days}d` : '—'}</td>
          </tr>
        ))}
      </Table>
    </div>
  )
}

function GradeCTable({ rows }) {
  return (
    <Table headers={['Product', 'Operation', 'Obs', 'Book rate', 'Dispersion (σ)', 'Actual hrs', 'Why grade C']}>
      {rows.map((r, i) => (
        <tr key={`${r.prod_code}-${r.op_code}-${i}`} className="hover:bg-gray-50 dark:hover:bg-gray-800/40">
          <td className="px-3 py-2">{r.prod_code}</td>
          <td className="px-3 py-2">{r.op_code}</td>
          <td className="px-3 py-2">{r.obs}</td>
          <td className={`px-3 py-2 ${r.book_rate < 80 ? 'text-red-600 dark:text-red-400' : ''}`}>{r.book_rate}%</td>
          <td className="px-3 py-2">{Number(r.log_sigma).toFixed(2)}</td>
          <td className="px-3 py-2">{Math.round(r.tot_act_hrs).toLocaleString()}</td>
          <td className="px-3 py-2 text-gray-500">{r.reason}</td>
        </tr>
      ))}
    </Table>
  )
}

function Control1Table({ rows, onOpen }) {
  if (!rows) return null
  const total = rows.length
  const valueExposed = rows.reduce((a, r) => a + (Number(r.order_value) || 0), 0)
  const importN = rows.filter((r) => r.binding_mode && r.binding_mode !== 'domestic').length
  const domesticN = total - importN
  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-800 p-4 text-sm text-red-800 dark:text-red-300">
        <span className="font-semibold">Control 1 — fabrication authorized without an approved drawing on file.</span>{' '}
        These orders have a completed RELEASE (fabrication is unlocked) but carry <b>no PartDraw attachment</b> — the signed
        drawing that should gate a build. Approval is meant to route Amtrend → client → back → approved → then RELEASE, so a
        released order with no drawing on file is a governance gap.
        <div className="mt-2 text-xs text-red-700 dark:text-red-400">
          <b>{total}</b> orders · <b>{formatCurrency(valueExposed, false)}</b> order value exposed · {domesticN} domestic · {importN} import-related
        </div>
        <div className="mt-1 text-xs text-red-700/90 dark:text-red-400/90">
          Scope: only <b>drawing-applicable</b> orders (routed through a drafting/approval op). Program / catalog SKUs — Anchor
          sofa, Verona, Tenor, Moon, Comfort — get a RELEASE but need no per-order drawing and are excluded. Evidence is the
          order-header attachment set; a drawing filed elsewhere would not be seen here.
        </div>
      </div>
      <Table headers={['Project / Site', 'Order', 'Mode', 'Owner', 'Order value', 'Released jobs', 'On file (no PartDraw)']}>
        {rows.map((r) => (
          <tr key={r.order_num} className="hover:bg-gray-50 dark:hover:bg-gray-800/40 cursor-pointer" onClick={() => onOpen(r.order_num)}>
            <td className="px-3 py-2"><ProjectCell project={r.project_name} customer={r.customer_name} /></td>
            <td className="px-3 py-2 font-mono text-xs">{r.order_num}</td>
            <td className="px-3 py-2">{MODE_BADGE[r.binding_mode] && <span className={`px-1.5 py-px rounded-full text-[10px] border ${MODE_BADGE[r.binding_mode].cls}`}>{MODE_BADGE[r.binding_mode].label}</span>}</td>
            <td className="px-3 py-2 text-xs">{r.binding_owner || '—'}</td>
            <td className="px-3 py-2 font-medium">{formatCurrency(r.order_value, false)}</td>
            <td className="px-3 py-2 text-xs">{r.released_jobs}</td>
            <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">{r.doctypes || <span className="italic text-red-500/80">nothing attached</span>}</td>
          </tr>
        ))}
      </Table>
    </div>
  )
}

function MissingApprovalTable({ rows, onOpen }) {
  if (!rows) return null
  const total = rows.length
  const orders = new Set(rows.map((r) => r.order_num)).size
  const importJobs = rows.filter((r) => r.mode !== 'domestic').length
  const actionable = rows.filter((r) => !r.release_done).length
  const byMode = {}
  for (const r of rows) byMode[r.mode] = (byMode[r.mode] || 0) + 1
  const modeStr = Object.entries(byMode).sort((a, b) => b[1] - a[1]).map(([m, n]) => `${m} ${n}`).join(' · ')
  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-800 p-4 text-sm text-red-800 dark:text-red-300">
        <span className="font-semibold">Control 2 — routing has no APPROVAL operation.</span>{' '}
        The fabrication routing carries RELEASE / OS-FAB but no APPROVAL gate, so the approval cycle (Amtrend → client → approved) is invisible and can't be aged — the reason an approval can sit for weeks unnoticed. This is a routing-template fix, not a per-order chase.
        <div className="mt-2 text-xs text-red-700 dark:text-red-400"><b>{total}</b> jobs across <b>{orders}</b> orders · {modeStr}</div>
        <div className="mt-1 text-xs text-red-700 dark:text-red-400">
          Priority: <b>{importJobs}</b> import jobs (approval matters most) · <b>{actionable}</b> not yet released — fixing those now restores an ageable clock; the rest are historical template debt.
        </div>
      </div>
      <Table headers={['Project / Site', 'Job', 'Order', 'Mode', 'Open gate', 'Released?']}>
        {rows.map((r) => (
          <tr key={r.job_num} className="hover:bg-gray-50 dark:hover:bg-gray-800/40 cursor-pointer" onClick={() => onOpen(r.order_num)}>
            <td className="px-3 py-2"><ProjectCell project={r.project_name} customer={r.customer_name} /></td>
            <td className="px-3 py-2 font-mono text-xs">{r.job_num}</td>
            <td className="px-3 py-2 font-mono text-xs">{r.order_num}</td>
            <td className="px-3 py-2">{MODE_BADGE[r.mode] && <span className={`px-1.5 py-px rounded-full text-[10px] border ${MODE_BADGE[r.mode].cls}`}>{MODE_BADGE[r.mode].label}</span>}</td>
            <td className="px-3 py-2 text-xs">{gateLabel(r.open_gate)}</td>
            <td className="px-3 py-2 text-xs">{r.release_done ? <span className="text-gray-400">released (debt)</span> : <span className="text-red-600 dark:text-red-400 font-medium">not yet — fix now</span>}</td>
          </tr>
        ))}
      </Table>
    </div>
  )
}

function ExitingTable({ rows }) {
  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-orange-300 bg-orange-50 dark:bg-orange-900/20 dark:border-orange-800 p-3 text-sm text-orange-800 dark:text-orange-300">
        <span className="font-medium">Exiting vendor — all open frames flagged elevated risk regardless of age.</span> An
        exiting customer loses factory priority, so even in-window POs are at risk. No new POs should be placed here.
      </div>
      <Table headers={['Project / Site', 'Vendor', 'PO', 'Part', 'Job', 'Raised', 'Age (days)', 'Past p50', 'Promise', 'Received']}>
        {rows.map((r, i) => (
          <tr key={`${r.po_num}-${r.po_line}-${r.po_rel_num}-${i}`} className="hover:bg-gray-50 dark:hover:bg-gray-800/40">
            <td className="px-3 py-2"><ProjectCell project={r.project_name} customer={r.customer_name} /></td>
            <td className="px-3 py-2">{r.vendor_name}</td>
            <td className="px-3 py-2 font-mono text-xs">{r.po_num}-{r.po_line}</td>
            <td className="px-3 py-2">{r.part_num}</td>
            <td className="px-3 py-2 font-mono text-xs">{r.job_num || '—'}</td>
            <td className="px-3 py-2">{formatDate(r.po_raised)}</td>
            <td className={`px-3 py-2 ${r.past_p50 ? 'text-red-600 dark:text-red-400 font-semibold' : ''}`}>{r.age_days}</td>
            <td className="px-3 py-2">{r.past_p50 ? 'yes' : ''}</td>
            <td className="px-3 py-2">
              {r.has_promise
                ? <span className={r.promise_passed ? 'text-red-600 dark:text-red-400 font-semibold' : ''}>{formatDate(r.promise_date)}{r.promise_passed ? ' (passed)' : ''}</span>
                : <span className="text-gray-400">none</span>}
            </td>
            <td className="px-3 py-2">{r.not_yet_received ? <span className="text-gray-400">no</span> : 'partial'}</td>
          </tr>
        ))}
      </Table>
    </div>
  )
}

function PartGateTable({ rows }) {
  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-blue-300 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800 p-3 text-sm text-blue-800 dark:text-blue-300">
        <span className="font-medium">PO-to-part frame gate.</span> These jobs consume a purchased frame (Interwood, Infinity
        anchor sofa) bought on a PO that carries no job link — an upstream gate invisible to the normal OS-FAB logic.
        Pegged by part number, so the ETA is directional.
      </div>
      <Table headers={['Project / Site', 'Job', 'Family', 'Part', 'Vendor', 'PO', 'Frame ETA', 'Days out', 'Promise?', 'Received']}>
        {rows.map((r, i) => (
          <tr key={`${r.job_num}-${r.part_num}-${i}`} className="hover:bg-gray-50 dark:hover:bg-gray-800/40">
            <td className="px-3 py-2"><ProjectCell project={r.project_name} customer={r.customer_name} /></td>
            <td className="px-3 py-2 font-mono text-xs">{r.job_num}</td>
            <td className="px-3 py-2">{r.family}</td>
            <td className="px-3 py-2">{r.part_num}</td>
            <td className="px-3 py-2 text-gray-500">{r.vendor_name}</td>
            <td className="px-3 py-2 font-mono text-xs">{r.po_num}-{r.po_line}</td>
            <td className={`px-3 py-2 ${r.eta_passed_unreceived ? 'text-red-600 dark:text-red-400 font-semibold' : ''}`}>{formatDate(r.eta)}</td>
            <td className="px-3 py-2">{r.eta_days_out}</td>
            <td className="px-3 py-2">{r.has_promise ? 'promise' : <span className="text-gray-400">est</span>}</td>
            <td className="px-3 py-2">{r.received ? 'yes' : <span className="text-gray-400">no</span>}</td>
          </tr>
        ))}
      </Table>
    </div>
  )
}

function ApprovalTable({ rows }) {
  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-purple-300 bg-purple-50 dark:bg-purple-900/20 dark:border-purple-800 p-3 text-sm text-purple-800 dark:text-purple-300">
        <span className="font-medium">Approval loop.</span> Submit→approve window from APPROVAL clock-ins (measurable on ~24% of
        jobs with multiple redlines). Two signals: <b>pending 20+ days</b> (chaseable — median cycle ~11d) and <b>3+ redline
        cycles</b> (a drawing-quality flag, higher on OS-DRAFT than in-house).
      </div>
      <Table headers={['Project / Site', 'Job', 'Open', 'Drafting', 'Submitted', 'Redlines', 'Days pending', 'Approved', 'Alert']}>
        {rows.map((r, i) => (
          <tr key={`${r.job_num}-${i}`} className="hover:bg-gray-50 dark:hover:bg-gray-800/40">
            <td className="px-3 py-2"><ProjectCell project={r.project_name} customer={r.customer_name} /></td>
            <td className="px-3 py-2 font-mono text-xs">{r.job_num}</td>
            <td className="px-3 py-2">{r.job_open ? 'yes' : 'no'}</td>
            <td className="px-3 py-2 text-gray-500">{r.drafting_source}</td>
            <td className="px-3 py-2">{formatDate(r.submitted)}</td>
            <td className={`px-3 py-2 ${r.redline_cycles >= 3 ? 'text-red-600 dark:text-red-400 font-semibold' : ''}`}>{r.redline_cycles}</td>
            <td className="px-3 py-2">{r.days_pending ?? '—'}</td>
            <td className="px-3 py-2">{r.approved ? 'yes' : <span className="text-amber-600 dark:text-amber-400">pending</span>}</td>
            <td className="px-3 py-2">
              {r.alert_pending_20d && <span className="px-1.5 py-0.5 rounded text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">PENDING 20d+</span>}
              {r.alert_redline_3plus && <span className="px-1.5 py-0.5 rounded text-xs bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">3+ REDLINES</span>}
            </td>
          </tr>
        ))}
      </Table>
    </div>
  )
}

function ValidationChart({ data }) {
  return (
    <div className="bg-white dark:bg-surface-dark rounded-lg border border-gray-200 dark:border-gray-700 p-5">
      <h3 className="font-semibold text-gray-900 dark:text-gray-100">Runway at the gate predicts lateness</h3>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        Historical jobs (completed since 2024), bucketed by days of slack when the RELEASE gate opened.
        Late rate falls monotonically as runway grows — the sole thing that separates on-time from late.
        This is why the monitor sorts by runway, not by due date.
      </p>
      <div style={{ width: '100%', height: 320 }}>
        <ResponsiveContainer>
          <BarChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
            <XAxis dataKey="slack_bucket" tick={{ fontSize: 12 }} />
            <YAxis unit="%" domain={[0, 80]} tick={{ fontSize: 12 }} />
            <Tooltip formatter={(v, n) => n === 'late_pct' ? [`${v}%`, 'Late rate'] : [v, n]} />
            <ReferenceLine y={45} stroke="#9ca3af" strokeDasharray="4 4" label={{ value: 'overall 43%', fontSize: 11, position: 'right' }} />
            <Bar dataKey="late_pct" radius={[4, 4, 0, 0]}>
              {data.map((d, i) => (
                <Cell key={i} fill={d.late_pct >= 55 ? '#dc2626' : d.late_pct >= 45 ? '#f59e0b' : '#059669'} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}
